/**
 * Realtime Monitor Component
 *
 * Integrates WebSocket monitoring for:
 * - Price alerts
 * - Whale activity
 *
 * Runs in the background when user is authenticated.
 */

import { useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAuth } from '../contexts/AuthContext';
import { useAlertMonitor } from '../hooks/useAlertMonitor';
import { useWhaleMonitor } from '../hooks/useWhaleMonitor';
import { useToast } from './Toast';
import { requestNotificationPermission } from '../lib/alerts';
import type { PriceAlert } from '../lib/alerts';
import type { TrackedWallet, WalletActivity } from '../lib/whales';

export function RealtimeMonitor() {
  const { publicKey } = useWallet();
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();

  const walletAddress = publicKey?.toString() || null;
  const enabled = isAuthenticated && !!walletAddress;

  // Request notification permission on mount
  useEffect(() => {
    if (enabled) {
      requestNotificationPermission();
    }
  }, [enabled]);

  // Handle triggered price alerts
  const handleAlertTriggered = (alert: PriceAlert) => {
    const action = alert.type === 'above' || alert.type === 'percent_up' ? 'hit target' : 'dropped';
    showToast('success', `${alert.tokenSymbol} ${action}! Check your alerts.`);
  };

  // Handle whale activity
  const handleWhaleActivity = (wallet: TrackedWallet, activity: WalletActivity) => {
    const action = activity.type === 'buy' ? 'bought' : 'sold';
    showToast('info', `${wallet.label} ${action} ${activity.tokenSymbol}`);
  };

  // Price alert monitoring
  useAlertMonitor({
    walletAddress,
    enabled,
    onAlertTriggered: handleAlertTriggered,
  });

  // Whale activity monitoring
  useWhaleMonitor({
    userWallet: walletAddress,
    enabled,
    onWhaleActivity: handleWhaleActivity,
  });

  // This component doesn't render anything
  return null;
}
